import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, ArrowRight } from 'lucide-react';
import { countryCodes } from '../data/mockData';

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', countryCode: '+1', phone: '', description: ''
  });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'email' || field === 'phone') setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Enter a valid email address' }));
    }
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 10) {
      handleChange('phone', digitsOnly);
      if (digitsOnly && digitsOnly.length !== 10) {
        setErrors(prev => ({ ...prev, phone: 'Must be exactly 10 digits' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { email: '', phone: '' };
    let hasErrors = false;
    if (!validateEmail(formData.email)) { newErrors.email = 'Enter a valid email'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }

    setIsSubmitting(true);
    setTimeout(() => {
      toast({ title: "Request Submitted!", description: "A trademark specialist will contact you within 24 hours." });
      setIsSubmitting(false);
      setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
      setErrors({ email: '', phone: '' });
      onClose();
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-[900px] p-0 gap-0 overflow-hidden border-2 border-[#111827] bg-white rounded-none shadow-[8px_8px_0px_#FF1010]">
        <div className="flex flex-col md:flex-row">
          {/* Left - Image */}
          <div className="hidden md:block w-[40%] relative">
            <img
              src="https://images.unsplash.com/photo-1716591082505-b8f8be290e1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxsZWdhbCUyMGRvY3VtZW50JTIwcGVuJTIwY2xvc2V1cHxlbnwwfHx8fDE3NzYxOTgwMjl8MA&ixlib=rb-4.1.0&q=85"
              alt="Legal document"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          {/* Right - Form */}
          <div className="w-full md:w-[60%] p-7 md:p-8">
            <div className="mb-5">
              <h2 className="text-xl font-black tracking-tighter text-[#111827] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>Get Your Free Consultation</h2>
              <p className="text-sm text-[#4B5563] mt-1">Fill this out. We call you within 24 hours.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label htmlFor="dialog-name" className="text-[#111827] text-xs font-bold uppercase tracking-wider">Full Name *</Label>
                <Input id="dialog-name" data-testid="dialog-name-input" placeholder="Your name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required className="h-10 bg-[#F9FAFB] border-[#E5E7EB] text-[#111827] placeholder:text-[#9CA3AF] focus:border-[#111827] focus:ring-1 focus:ring-[#111827] rounded-none mt-1" />
              </div>

              <div>
                <Label htmlFor="dialog-email" className="text-[#111827] text-xs font-bold uppercase tracking-wider">Email *</Label>
                <Input id="dialog-email" data-testid="dialog-email-input" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} onBlur={handleEmailBlur} required className={`h-10 bg-[#F9FAFB] text-[#111827] placeholder:text-[#9CA3AF] rounded-none mt-1 ${errors.email ? 'border-[#FF1010]' : 'border-[#E5E7EB]'} focus:border-[#111827] focus:ring-1 focus:ring-[#111827]`} />
                {errors.email && <p className="text-[#FF1010] text-xs mt-0.5 font-medium">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="dialog-phone" className="text-[#111827] text-xs font-bold uppercase tracking-wider">Phone *</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={formData.countryCode} onValueChange={(v) => handleChange('countryCode', v)}>
                    <SelectTrigger data-testid="dialog-country-select" className="w-[100px] h-10 bg-[#F9FAFB] border-[#E5E7EB] text-[#111827] focus:border-[#111827] rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] bg-white border-[#E5E7EB]">
                      {countryCodes.map((item) => (
                        <SelectItem key={item.code} value={item.code} className="text-[#111827]">
                          <span className="flex items-center gap-2"><span>{item.flag}</span><span>{item.code}</span></span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input id="dialog-phone" data-testid="dialog-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 bg-[#F9FAFB] text-[#111827] placeholder:text-[#9CA3AF] rounded-none ${errors.phone ? 'border-[#FF1010]' : 'border-[#E5E7EB]'} focus:border-[#111827] focus:ring-1 focus:ring-[#111827]`} />
                </div>
                {errors.phone && <p className="text-[#FF1010] text-xs mt-0.5 font-medium">{errors.phone}</p>}
              </div>

              <div>
                <Label htmlFor="dialog-desc" className="text-[#111827] text-xs font-bold uppercase tracking-wider">What Do You Need?</Label>
                <Textarea id="dialog-desc" data-testid="dialog-description-input" placeholder="e.g. Register my brand name and logo with the USPTO" rows={2} value={formData.description} onChange={(e) => handleChange('description', e.target.value)} className="bg-[#F9FAFB] border-[#E5E7EB] text-[#111827] placeholder:text-[#9CA3AF] focus:border-[#111827] focus:ring-1 focus:ring-[#111827] resize-none rounded-none mt-1" />
              </div>

              <Button type="submit" data-testid="dialog-submit-button" className="w-full bg-[#FF1010] hover:bg-[#CC0000] text-white font-black h-11 text-sm rounded-none uppercase tracking-widest" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  <>Submit Request <ArrowRight className="ml-2 w-4 h-4" /></>
                )}
              </Button>

              <p className="text-[10px] text-[#9CA3AF] text-center">
                By submitting, you agree to our Terms and Privacy Policy.
              </p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
